import {type Viewport} from '@packages/systems/open-canvas/core';
import {type Box, create as createBox} from '@packages/utilities/box';

type TransformToolbarAnchorArgs = {
  /**
   * Required for default scroll-based page computations
   */
  canvasScale?: number;
  /**
   * Required for open-canvas computations
   */
  viewport?: Viewport | null;
};

type TransformClientRectArgs = {
  nativeNode: HTMLElement | null;
};

/**
 * The `ContentEditorViewportAdapter` interface is responsible for providing the bounds transformation logic for DOM bounds measured by the `ContentEditor` implementation.
 * Classes that implement this interface are responsible for transforming the provided bounds for the current viewport type.
 * @see {@link ContentEditorOpenCanvasViewportAdapter}
 * @see {@link ContentEditorPageViewportAdapter}
 */
export interface ContentEditorViewportAdapter {
  /**
   * Transforms the provided bounds (measured using `getBoundingClientRect`) for the current viewport type.
   */
  transformClientRect(bounds: ClientRect, args: TransformClientRectArgs): Box;
  /**
   * Transforms the provided selection range bounds for the current viewport type.
   */
  transformRangeClientRect(
    bounds: ClientRect,
    args: TransformClientRectArgs
  ): Box;
  /**
   * Transforms the position of the Content Editor's toolbar anchor for the current viewport type.
   */
  transformToolbarAnchor(bounds: Box, args: TransformToolbarAnchorArgs): Box;
}

type ContentEditorViewportAdapterCtorArgs = {
  getViewport: () => Viewport;
};

/**
 * Provides transformation logic for bounds measured in `ContentEditor` for the Open Canvas viewport.
 */
export class ContentEditorOpenCanvasViewportAdapter implements ContentEditorViewportAdapter {
  private _getViewport: () => Viewport;

  constructor(args: ContentEditorViewportAdapterCtorArgs) {
    this._getViewport = args.getViewport;
  }

  public transformClientRect(bounds: ClientRect): Box {
    const viewport = this._getViewport();

    return {
      top: bounds.top / viewport.k + viewport.y,
      left: bounds.left / viewport.k + viewport.x,
      width: bounds.width / viewport.k,
      height: bounds.height / viewport.k,
    };
  }

  public transformRangeClientRect(bounds: ClientRect): Box {
    return this.transformClientRect(bounds);
  }

  public transformToolbarAnchor(
    bounds: Box,
    {viewport}: TransformToolbarAnchorArgs
  ): Box {
    if (!viewport) return bounds;

    return {
      top: (bounds.top - viewport.y) * viewport.k,
      left: (bounds.left - viewport.x) * viewport.k,
      width: bounds.width * viewport.k,
      height: bounds.height * viewport.k,
    };
  }
}

/**
 * Provides transformation logic for bounds measured in `ContentEditor` for the standard scroll-based page viewport.
 */
export class ContentEditorPageViewportAdapter implements ContentEditorViewportAdapter {
  public transformClientRect(
    bounds: ClientRect,
    {nativeNode}: TransformClientRectArgs
  ): Box {
    const {offsetTop, offsetLeft} = nativeNode
      ? getNativeElementOffsetsFromScrollContainer(nativeNode)
      : {offsetTop: 0, offsetLeft: 0};

    const nodeClientRect =
      bounds && reifyClientRectWithOffsets(bounds, offsetTop, offsetLeft);
    return nodeClientRect;
  }

  public transformRangeClientRect(
    bounds: ClientRect,
    {nativeNode}: TransformClientRectArgs
  ): Box {
    // NOTE: We really want the offset of the range from the scrollContainer
    // so that the clientRect doesn't become invalid as soon as we scroll
    // but since ranges don't have offsetTop/offsetLeft we get the diff from
    // the native subtree node and add that to the native subtree's offsets.
    if (bounds && nativeNode) {
      const nativeSubtreeClientRect = nativeNode.getBoundingClientRect();

      const {offsetTop, offsetLeft} =
        getNativeElementOffsetsFromScrollContainer(nativeNode);
      const rangeOffsetTop =
        offsetTop + (bounds.top - nativeSubtreeClientRect.top);
      const rangeOffsetLeft =
        offsetLeft + (bounds.left - nativeSubtreeClientRect.left);

      return reifyClientRectWithOffsets(
        bounds,
        rangeOffsetTop,
        rangeOffsetLeft
      );
    } else {
      // In some cases there's temporarily no native clientRect from the range
      // so just return a blank reified
      return bounds;
    }
  }

  public transformToolbarAnchor(
    bounds: Box,
    {canvasScale}: TransformToolbarAnchorArgs
  ): Box {
    if (!canvasScale) return bounds;

    return {
      top: bounds.top * canvasScale,
      left: bounds.left * canvasScale,
      width: bounds.width * canvasScale,
      height: bounds.height * canvasScale,
    };
  }
}

export const defaultContentEditorViewportAdapter =
  new ContentEditorPageViewportAdapter();

// INTERNAL HELPER UTILS BELOW

const reifyClientRectWithOffsets = (
  clientRect: ClientRect | null | undefined,
  topOffset: number,
  leftOffset: number
): Box => {
  return {
    ...createBox(clientRect),
    top: topOffset,
    left: leftOffset,
  };
};

const getNativeElementOffsetsFromScrollContainer = (
  nativeEl: HTMLElement
): {
  offsetTop: number;
  offsetLeft: number;
} => {
  let {offsetTop, offsetLeft} = nativeEl;
  let offsetEl = nativeEl.offsetParent;

  // kit-scrollbar is the scroll container in the data-manager, otherwise it'll go all the way up to the body
  // element (which we want in the designer/editor canvases)
  while (offsetEl && !offsetEl.className.includes('kit-scrollbar')) {
    // When an element has a translate 3d property it changes the parent child
    // relationship of some position absolute and position relative nodes.
    // The translate3d element becomes the offsetParent of a node with position absolute.
    // We need to break the offset calculation here to get the correct offset.
    const elStyleAttr = offsetEl.getAttribute('style');
    if (elStyleAttr && elStyleAttr.includes('translate3d')) {
      break;
    }
    // In a slider, each slide has an offset of `slide index * slider width`; the slider uses a css transform to
    // show the selected slide. To position the editor toolbar correctly, we need to ignore this extra offset.
    // See issue: https://github.com/webflow/webflow/issues/27973
    if (!offsetEl.classList.contains('w-slide')) {
      // @ts-expect-error - TS2339 - Property 'offsetTop' does not exist on type 'Element'.
      offsetTop += offsetEl.offsetTop;
      // @ts-expect-error - TS2339 - Property 'offsetLeft' does not exist on type 'Element'.
      offsetLeft += offsetEl.offsetLeft;
    }
    // @ts-expect-error - TS2339 - Property 'offsetParent' does not exist on type 'Element'.
    offsetEl = offsetEl.offsetParent;
  }

  return {
    offsetTop,
    offsetLeft,
  };
};
